import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { BattleState } from '@somnhold/sim';
import { HIP_X, HIP_Y } from '../models/villagers.ts';
import { WAYFARER_LOOKS, wayfarerBody, wayfarerLeg } from '../models/wayfarers.ts';
import { facingToRotY, SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import type { OmenCtx } from '../omens/common.ts';

/**
 * The collapse (La Quemadura 1, docs/QUEMADURA.md): the glass at the Burn's edge breaking from one side of the field.
 *
 * - The ground: every glassed surface of the board (tiles, outskirts, roads, woods, spires) drops away past the break
 *   into a dark void, its broken edge glowing ember red; a band of glowing cracks runs ahead of it, brighter the
 *   closer it gets; the firm rock on the far side is painted pale grey stone with a chalk line where it starts. All of
 *   it in the materials' own shaders (`sink`), moved by a handful of uniforms: no geometry or shader made per frame.
 * - Cairns of the wayfarers along the rock's edge (one instanced draw).
 * - The patrol: lying on the glass with a pulsing red ring that fills while a hero stays by them; limping after their
 *   hero once lifted; sitting on the rock once safe. Two instanced draws (bodies by look, legs) and one ring draw.
 * - Embers off the broken edge, shards falling at every crack, a puff of haze when one of them is found.
 */

const K = WORLD_PER_HEX / 1000;
const BIG = 1.15;
const MAX = 8;
const WARM = new THREE.Color('#ffcf9e');
const EMBER = new THREE.Color('#ff6a3a');
const RED = new THREE.Color('#ff4f3a');
/** How far the broken glass drops (world units): out of sight under the fog. */
const DROP = 7;

const SINK_PARS = /* glsl */ `
uniform vec2 uColAxis;
uniform float uColF;
uniform float uColRock;
uniform float uColTime;
varying float vColA;
varying float vColDrop;
varying vec2 vColP;`;

const SINK_VERT = /* glsl */ `
{
  vec4 cw = vec4(transformed, 1.0);
  #ifdef USE_INSTANCING
  cw = instanceMatrix * cw;
  #endif
  cw = modelMatrix * cw;
  vColA = dot(cw.xz, uColAxis);
  vColP = cw.xz;
  vColDrop = smoothstep(uColF, uColF + 0.45, vColA);
  transformed.y -= vColDrop * ${DROP.toFixed(1)};
}`;

/** Past the break: dark, its cliff glowing; nothing else (woods, spires, roads). */
const SINK_FRAG = /* glsl */ `
{
  // vColDrop runs 0 at the broken edge's top to 1 at its foot: the cliff glows at its lip and goes dark below.
  diffuseColor.rgb *= mix(1.0, 0.05, vColDrop);
  float lip = exp(-abs(vColA - uColF - 0.1) * 5.0) * (1.0 - smoothstep(0.0, 0.35, vColDrop));
  totalEmissiveRadiance += vec3(1.0, 0.33, 0.12) * lip * 1.1 + vec3(0.5, 0.12, 0.04) * (1.0 - vColDrop) * step(0.001, vColDrop) * 0.25;
}`;

/** The ground also: the cracks running ahead of the break, and the firm rock past the chalk line. */
const GROUND_FRAG = /* glsl */ `
{
  vec2 p = vColP;
  float near = 1.0 - smoothstep(uColF - 3.2, uColF - 0.05, vColA);
  float band = (1.0 - near) * step(vColA, uColF + 0.05);
  vec4 vn = texture2D(tVein, p * 0.21 + vec2(0.13, 0.71));
  float crack = 1.0 - smoothstep(0.0, mix(0.01, 0.045, band), vn.r);
  float beat = 0.65 + 0.35 * sin(uColTime * 7.0 + vn.g * 20.0);
  totalEmissiveRadiance += vec3(1.0, 0.36, 0.12) * crack * band * band * beat * 1.4;
  // The firm rock: pale grey stone, mottled, and a chalk line where it begins.
  float rock = smoothstep(uColRock + 0.35, uColRock - 0.35, vColA);
  vec4 rv = texture2D(tVein, p * 0.17);
  vec3 stone = vec3(0.21, 0.215, 0.225) * (0.8 + 0.4 * rv.g) * (1.0 - 0.55 * (1.0 - smoothstep(0.0, 0.02, rv.r)));
  diffuseColor.rgb = mix(diffuseColor.rgb, stone, rock);
  float line = exp(-abs(vColA - uColRock) * 22.0) * step(0.5, fract(dot(p, vec2(-uColAxis.y, uColAxis.x)) * 1.3));
  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.62, 0.62, 0.64), line * 0.7);
}`;

const RING_VERT = /* glsl */ `
attribute vec4 iRing; // x, y, z, radius
attribute vec4 iTint; // r, g, b, fill (−1: the red «lift me» pulse)
varying vec2 vUv;
varying vec4 vTint;
void main() {
  vUv = uv;
  vTint = iTint;
  vec3 p = iRing.xyz + vec3(position.x, 0.0, -position.y) * iRing.w * 2.0;
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
}`;

const RING_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vUv;
varying vec4 vTint;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float a;
  vec3 c = vTint.rgb;
  if (vTint.a < -0.5) {
    float pulse = fract(uTime * 0.6);
    float rim = smoothstep(0.8, 0.9, r) * (1.0 - smoothstep(0.94, 1.0, r));
    float wave = smoothstep(0.0, 0.06, r - pulse * 0.9) * (1.0 - smoothstep(0.06, 0.18, r - pulse * 0.9)) * (1.0 - pulse);
    a = rim * (0.5 + 0.3 * sin(uTime * 3.0)) + wave * 0.5 * step(r, 0.95);
  } else {
    float band = smoothstep(0.7, 0.75, r) * (1.0 - smoothstep(0.88, 0.93, r));
    float ang = fract(atan(p.x, p.y) / 6.2831853 + 0.5);
    float filled = step(ang, vTint.a);
    c = mix(vec3(0.85, 0.86, 0.92), vec3(1.0, 0.82, 0.6), filled);
    a = band * mix(0.3, 1.0, filled);
  }
  if (a < 0.01) discard;
  gl_FragColor = vec4(c, a);
  #include <colorspace_fragment>
}`;

type Rules = NonNullable<NonNullable<BattleState['rules']>['collapse']>;

interface Track {
  px: number;
  py: number;
  cx: number;
  cy: number;
  dist: number;
}

/** A cairn of the wayfarers: four stones stacked, the top one pale, as one geometry. */
const cairnGeo = (): THREE.BufferGeometry => {
  const parts: THREE.BufferGeometry[] = [];
  const sizes = [0.26, 0.2, 0.15, 0.1];
  let y = 0;
  sizes.forEach((s, i) => {
    const g = new THREE.DodecahedronGeometry(s, 0).scale(1.2, 0.6, 1).rotateY(i * 1.3).translate(i % 2 ? 0.03 : -0.02, y + s * 0.5, 0);
    const col = new THREE.Color(i === 3 ? '#d8d4cc' : i % 2 ? '#8e8a84' : '#76726c');
    const n = g.getAttribute('position').count;
    const c = new Float32Array(n * 3);
    for (let k = 0; k < n; k++) c.set([col.r, col.g, col.b], k * 3);
    g.setAttribute('color', new THREE.BufferAttribute(c, 3));
    parts.push(g.index ? g.toNonIndexed() : g);
    y += s * 0.95;
  });
  return mergeGeometries(parts, false) as THREE.BufferGeometry;
};

export class CollapseLayer {
  readonly group = new THREE.Group();
  /** Shared by every sunk material: the break's way and place, the rock line, the time. */
  readonly uniforms = { uColAxis: { value: new THREE.Vector2(1, 0) }, uColF: { value: 1e4 }, uColRock: { value: -1e4 }, uColTime: { value: 0 } };
  private bodies: THREE.InstancedMesh[];
  private legs: THREE.InstancedMesh;
  private mat: THREE.MeshStandardMaterial;
  private cairns: THREE.InstancedMesh;
  private rings: THREE.InstancedBufferGeometry;
  private ringPos = new Float32Array(MAX * 2 * 4);
  private ringTint = new Float32Array(MAX * 2 * 4);
  private ringMat: THREE.ShaderMaterial;
  private glow: GlowLayer;
  private track = new Map<number, Track>();
  private axis: THREE.Vector2;
  private prevF: number;
  private curF: number;
  private reach: number;
  private now = 0;
  private sunk = new WeakSet<THREE.Material>();
  private m = new THREE.Matrix4();
  private m2 = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private e = new THREE.Euler();

  constructor(
    private ctx: OmenCtx,
    private r: Rules,
    /** How far the field reaches from the node (world). */
    reach: number,
  ) {
    this.group.name = 'collapse';
    this.reach = reach;
    this.axis = new THREE.Vector2(r.ax / 10000, r.ay / 10000).normalize();
    this.uniforms.uColAxis.value.copy(this.axis);
    this.prevF = this.curF = r.from * K;
    this.uniforms.uColF.value = this.curF;
    this.uniforms.uColRock.value = r.rock * K;
    this.mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true, emissive: '#2a1e18', emissiveIntensity: 1 });
    this.bodies = Array.from({ length: WAYFARER_LOOKS }, (_, k) => {
      const im = new THREE.InstancedMesh(wayfarerBody(k), this.mat, 3);
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      im.count = 0;
      im.frustumCulled = false;
      this.group.add(im);
      return im;
    });
    this.legs = new THREE.InstancedMesh(wayfarerLeg(), this.mat, MAX * 2);
    this.legs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.legs.count = 0;
    this.legs.frustumCulled = false;
    this.group.add(this.legs);
    // Cairns every couple of paces along the rock's edge, inside the field.
    const perp = new THREE.Vector2(-this.axis.y, this.axis.x);
    const line = r.rock * K - 0.5;
    const spots: THREE.Vector3[] = [];
    for (let t = -reach; t <= reach; t += 2.1) {
      const x = this.axis.x * line + perp.x * t;
      const z = this.axis.y * line + perp.y * t;
      if (Math.hypot(x, z) > reach - 0.4) continue;
      spots.push(new THREE.Vector3(x, ctx.ground(x, z), z));
    }
    this.cairns = new THREE.InstancedMesh(cairnGeo(), new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true }), Math.max(1, spots.length));
    spots.forEach((p, i) => {
      this.q.setFromAxisAngle(this.v.set(0, 1, 0), i * 2.3);
      const s = 1.0 + (i % 3) * 0.15;
      this.cairns.setMatrixAt(i, this.m.compose(p, this.q, this.s3.set(s, s, s)));
    });
    this.cairns.count = spots.length;
    this.cairns.instanceMatrix.needsUpdate = true;
    this.group.add(this.cairns);
    this.rings = new THREE.InstancedBufferGeometry();
    const quad = new THREE.PlaneGeometry(1, 1);
    this.rings.index = quad.index;
    this.rings.setAttribute('position', quad.getAttribute('position'));
    this.rings.setAttribute('uv', quad.getAttribute('uv'));
    this.rings.setAttribute('iRing', new THREE.InstancedBufferAttribute(this.ringPos, 4).setUsage(THREE.DynamicDrawUsage));
    this.rings.setAttribute('iTint', new THREE.InstancedBufferAttribute(this.ringTint, 4).setUsage(THREE.DynamicDrawUsage));
    this.rings.instanceCount = 0;
    this.ringMat = new THREE.ShaderMaterial({ vertexShader: RING_VERT, fragmentShader: RING_FRAG, uniforms: { uTime: { value: 0 } }, transparent: true, depthWrite: false });
    const rm = new THREE.Mesh(this.rings, this.ringMat);
    rm.frustumCulled = false;
    rm.renderOrder = 6;
    this.group.add(rm);
    this.glow = new GlowLayer(ctx.low ? 24 : 40);
    this.group.add(this.glow.mesh);
  }

  /**
   * Makes the meshes under `o` drop away past the break (once per material). `ground` ones (they must carry the
   * glass's `tVein` uniform) also show the cracks ahead of it and the firm rock.
   */
  sink(o: THREE.Object3D, ground: boolean): void {
    o.traverse((x) => {
      const mesh = x as THREE.Mesh;
      if (!mesh.isMesh || (x as unknown as THREE.Sprite).isSprite) return;
      const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of list) {
        const sm = mat as THREE.MeshStandardMaterial;
        if (!sm || this.sunk.has(sm) || !(sm.isMeshStandardMaterial || (sm as unknown as THREE.MeshLambertMaterial).isMeshLambertMaterial)) continue;
        this.sunk.add(sm);
        const prev = sm.onBeforeCompile.bind(sm);
        const prevKey = sm.customProgramCacheKey.bind(sm);
        const u = this.uniforms;
        sm.onBeforeCompile = (shader, renderer) => {
          prev(shader, renderer);
          Object.assign(shader.uniforms, u);
          shader.vertexShader = shader.vertexShader.replace('#include <common>', `#include <common>\n${SINK_PARS}`).replace('#include <begin_vertex>', `#include <begin_vertex>\n${SINK_VERT}`);
          shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>', `#include <common>\n${SINK_PARS}`)
            .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n${ground ? GROUND_FRAG : ''}\n${SINK_FRAG}`);
        };
        sm.customProgramCacheKey = () => `${prevKey()}|collapse${ground ? 'G' : ''}`;
        sm.needsUpdate = true;
      }
    });
  }

  /** Every fresh sim tick: where the break is now, and where each lifted one was and is. */
  tick(s: BattleState): void {
    this.prevF = this.curF;
    this.curF = (s.collapse?.f ?? this.r.from) * K;
    const seen = new Set<number>();
    for (const p of s.collapse?.people ?? []) {
      seen.add(p.id);
      const t = this.track.get(p.id);
      if (!t) this.track.set(p.id, { px: p.x, py: p.y, cx: p.x, cy: p.y, dist: 0 });
      else {
        t.dist += Math.hypot(p.x - t.cx, p.y - t.cy) * K;
        t.px = t.cx;
        t.py = t.cy;
        t.cx = p.x;
        t.cy = p.y;
      }
    }
    for (const id of [...this.track.keys()]) if (!seen.has(id)) this.track.delete(id);
    // Embers off the broken edge, now and then along it.
    if (s.tick % 2 === 0 && s.collapse && s.collapse.f > this.r.rock) {
      const p = this.rimPoint((s.tick * 7919) % 1000 / 1000);
      if (p) this.ctx.vfx.burst(p.setY(p.y + 0.1), { n: this.ctx.low ? 2 : 4, color: '#ff7a3a', speed: 0.012, up: 0.045, size: 0.06, life: 26, gravity: -0.0015, spread: 0.6 });
    }
  }

  /** A point on the broken edge (t from 0 to 1 across the field), on the ground; null if outside the field. */
  private rimPoint(t: number): THREE.Vector3 | null {
    const perp = this.v.set(-this.axis.y, 0, this.axis.x);
    const span = (t * 2 - 1) * this.reach;
    const x = this.axis.x * this.curF + perp.x * span;
    const z = this.axis.y * this.curF + perp.z * span;
    if (Math.hypot(x, z) > this.reach + 2) return null;
    return new THREE.Vector3(x, this.ctx.ground(x, z), z);
  }

  /** The collapse's moments: a crack (shards falling off the edge), lifted, found in the haze, safe, lost. */
  event(kind: 'crack' | 'fall' | 'lost' | 'safe' | 'lift' | 'found', x: number, y: number): void {
    const low = this.ctx.low;
    if (kind === 'crack') {
      for (let i = 0; i < (low ? 1 : 2); i++) {
        const p = this.rimPoint(Math.random());
        if (!p) continue;
        this.ctx.vfx.burst(p.setY(p.y + 0.05), { n: low ? 4 : 8, color: '#17121a', speed: 0.03, up: 0.03, size: 0.09, life: 24, gravity: 0.012, spread: 0.6 });
        this.ctx.vfx.burst(p, { n: low ? 2 : 4, color: '#ff8a4a', speed: 0.02, up: 0.05, size: 0.05, life: 16, gravity: 0.004, spread: 0.5 });
      }
      return;
    }
    const p = this.ctx.at(x, y, 0.1);
    if (kind === 'lift') {
      this.ctx.vfx.burst(p, { n: low ? 8 : 16, color: '#5a5660', speed: 0.035, up: 0.05, size: 0.08, life: 20, gravity: 0.006, spread: 0.4 });
      this.ctx.vfx.ring(this.ctx.at(x, y, 0.05), 0.2, 1.4, '#ffcf9e', 12, 0.6);
    } else if (kind === 'found') {
      this.ctx.vfx.burst(this.ctx.at(x, y, 0.6), { n: low ? 6 : 14, color: '#a9a4b0', speed: 0.02, up: 0.02, size: 0.5, life: 34, smoke: true, spread: 0.6 });
      this.ctx.vfx.ring(this.ctx.at(x, y, 0.05), 0.2, 2.0, '#ff8a6e', 18, 0.7);
      this.ctx.flare(this.ctx.at(x, y, 1.0), '#ffd9c0', 2.6, 16);
    } else if (kind === 'safe') {
      this.ctx.flare(this.ctx.at(x, y, 0.8), '#ffd9b0', 3.2, 18);
      this.ctx.vfx.ring(this.ctx.at(x, y, 0.05), 0.3, 1.8, '#ffcf9e', 14, 0.7);
      this.ctx.vfx.burst(this.ctx.at(x, y, 0.5), { n: low ? 8 : 16, color: '#ffd9b0', speed: 0.02, up: 0.05, size: 0.06, life: 26, gravity: -0.002, spread: 0.4 });
    } else if (kind === 'lost') {
      this.ctx.vfx.burst(this.ctx.at(x, y, 0.4), { n: low ? 6 : 12, color: '#3a3438', speed: 0.015, up: 0.03, size: 0.4, life: 40, smoke: true, spread: 0.3 });
      this.ctx.vfx.burst(p, { n: low ? 4 : 8, color: '#ff6a3a', speed: 0.02, up: 0.04, size: 0.06, life: 18, gravity: 0.006, spread: 0.4 });
    } else {
      // A nightmare going down with the glass: dark shards and a gust of embers where it was.
      this.ctx.vfx.burst(p, { n: low ? 4 : 8, color: '#ff7a3a', speed: 0.025, up: 0.06, size: 0.06, life: 20, gravity: 0.004, spread: 0.5 });
    }
  }

  /** Every redraw: the break where it is between ticks, the patrol, their rings and bars. */
  draw(s: BattleState, alpha: number, contact: (x: number, y: number, z: number, size: number) => void, bar: (p: THREE.Vector3, frac: number) => void): void {
    this.uniforms.uColF.value = this.prevF + (this.curF - this.prevF) * alpha;
    const counts = new Array<number>(WAYFARER_LOOKS).fill(0);
    let legs = 0;
    let rings = 0;
    const ring = (x: number, y: number, z: number, rad: number, c: THREE.Color, fill: number): void => {
      if (rings >= MAX * 2) return;
      this.ringPos.set([x, y, z, rad], rings * 4);
      this.ringTint.set([c.r, c.g, c.b, fill], rings * 4);
      rings++;
    };
    /** One wayfarer: `pose` 0 standing (walking with `stride`, limping), 1 lying on the back, 2 sitting. */
    const person = (look: number, g: THREE.Vector3, yaw: number, pose: 0 | 1 | 2, stride: number): void => {
      const body = this.bodies[look] as THREE.InstancedMesh;
      const i = counts[look] as number;
      if (i >= 3) return;
      counts[look] = i + 1;
      const sc = SCALE.hero * BIG;
      // The base: on the ground, turned; then the pose.
      this.q.setFromEuler(this.e.set(0, yaw, 0, 'YXZ'));
      const base = new THREE.Matrix4().compose(g, this.q, this.s3.set(sc, sc, sc));
      const pose3 = new THREE.Matrix4();
      let legSwing = 0;
      let legPitch = 0;
      if (pose === 1) {
        // On the back, head toward −Z of its turn, breathing.
        const breathe = 1 + Math.sin(this.now * 2.1 + look) * 0.015;
        pose3.compose(this.v.set(0, 0.16, 0.85), this.q.setFromEuler(this.e.set(-Math.PI / 2 + 0.06, 0, 0)), this.s3.set(1, breathe, 1));
      } else if (pose === 2) {
        // Sitting, leaning back on the pack, legs out in front.
        pose3.compose(this.v.set(0, -HIP_Y + 0.2, 0), this.q.setFromEuler(this.e.set(-0.28, 0, 0)), this.s3.set(1, 1, 1));
        legPitch = -Math.PI / 2 + 0.15;
      } else {
        // A limp: the bad leg short, the body dipping on it.
        const dip = Math.max(0, Math.sin(stride)) * 0.05;
        pose3.compose(this.v.set(0, Math.abs(Math.sin(stride)) * 0.02 - dip, 0), this.q.setFromEuler(this.e.set(0.08, 0, Math.sin(stride) * 0.06)), this.s3.set(1, 1, 1));
        legSwing = Math.sin(stride) * 0.5;
      }
      const bm = this.m.multiplyMatrices(base, pose3);
      body.setMatrixAt(i, bm);
      for (const sd of [-1, 1]) {
        if (legs >= MAX * 2) break;
        let lp: THREE.Matrix4;
        if (pose === 1) lp = this.m2.compose(this.v.set(sd * HIP_X, HIP_Y, 0), this.q.setFromEuler(this.e.set(0, 0, sd * 0.08)), this.s3.set(1, 1, 1));
        else lp = this.m2.compose(this.v.set(sd * HIP_X, HIP_Y, 0), this.q.setFromEuler(this.e.set(legPitch + legSwing * sd * (sd > 0 ? 0.6 : 1), 0, 0)), this.s3.set(1, 1, 1));
        this.legs.setMatrixAt(legs++, new THREE.Matrix4().multiplyMatrices(base, new THREE.Matrix4().multiplyMatrices(pose3, lp)));
      }
      contact(g.x, g.y, g.z, pose === 1 ? 0.75 : 0.55);
    };
    this.glow.begin();
    const st = s.collapse;
    const people = st?.people ?? this.r.spots.map((p, i) => ({ id: i + 1, x: p.x, y: p.y, hp: this.r.hp, maxHp: this.r.hp, pid: '', held: 0, facing: 0, moving: false, safe: false, at: p.at }));
    for (const p of people) {
      if (p.hp <= 0 || s.waveT < p.at) continue;
      const look = (p.id - 1) % WAYFARER_LOOKS;
      const t = this.track.get(p.id);
      const sx = t ? t.px + (t.cx - t.px) * alpha : p.x;
      const sy = t ? t.py + (t.cy - t.py) * alpha : p.y;
      const g = this.ctx.at(sx, sy, 0);
      if (p.safe) {
        // Sat down on the rock, facing back toward the break.
        person(look, g, Math.atan2(this.axis.x, this.axis.y), 2, 0);
        continue;
      }
      if (!p.pid) {
        // Lying where they fell: a red pulse that says «lift me», and the ring filling while a hero stays by.
        person(look, g, (p.id * 1.7) % 6.28, 1, 0);
        const rad = (this.r.lift * K) / 1.6;
        ring(g.x, g.y + 0.06, g.z, rad * 1.35, RED, -1);
        if (p.held > 0) ring(g.x, g.y + 0.07, g.z, rad, WARM, Math.min(1, p.held / this.r.ticks));
        this.glow.add(g.x, g.y + 0.4, g.z, EMBER, 1.1, 0.22 + 0.1 * Math.sin(this.now * 3 + p.id));
      } else {
        person(look, g, facingToRotY(p.facing), 0, p.moving ? (t?.dist ?? 0) * 4.6 : 0);
      }
      bar(this.v.set(g.x, g.y + (p.pid ? 1.45 : 0.6) * SCALE.hero * BIG + 0.12, g.z).clone(), Math.max(0, p.hp / p.maxHp));
    }
    // The firm rock: a faint warm light over its edge while anyone is still out on the glass.
    const out = people.some((p) => !p.safe && p.hp > 0);
    if (out) {
      const c = this.ctx.at(Math.round((this.r.ax * (this.r.rock - 1400)) / 10000), Math.round((this.r.ay * (this.r.rock - 1400)) / 10000), 0);
      this.glow.add(c.x, c.y + 1.6, c.z, WARM, 3.2, 0.12 + 0.04 * Math.sin(this.now * 2));
    }
    this.glow.end();
    for (const b of this.bodies) b.instanceMatrix.needsUpdate = true;
    this.bodies.forEach((b, i) => (b.count = counts[i] as number));
    this.legs.count = legs;
    this.legs.instanceMatrix.needsUpdate = true;
    this.rings.instanceCount = rings;
    (this.rings.getAttribute('iRing') as THREE.InstancedBufferAttribute).needsUpdate = true;
    (this.rings.getAttribute('iTint') as THREE.InstancedBufferAttribute).needsUpdate = true;
  }

  /** Which way a nightmare going down with the glass tips: on along the break's way. */
  fallDir(): THREE.Vector3 {
    return new THREE.Vector3(this.axis.x, 0, this.axis.y);
  }

  frame(now: number): void {
    this.now = now;
    this.uniforms.uColTime.value = now;
    (this.ringMat.uniforms.uTime as { value: number }).value = now;
  }

  dispose(): void {
    for (const b of this.bodies) {
      b.geometry.dispose();
      b.dispose();
    }
    this.legs.geometry.dispose();
    this.legs.dispose();
    this.cairns.geometry.dispose();
    (this.cairns.material as THREE.Material).dispose();
    this.cairns.dispose();
    this.mat.dispose();
    this.rings.dispose();
    this.ringMat.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
